import { useState } from "react";
import useCustomEffect from "../../../hooks/useCustomEffect";
import useEvaApi, { EvaApiCall } from "../../../hooks/useEvaApi";
import PaxtonConfigsPageView from "./PaxtonConfigsPageView";

export default function PaxtonConfigsPage() {
  const callEvaApi = useEvaApi();
  const [submitError, setSubmitError] = useState(null);
  const [visitorTypes, setVisitorTypes] = useState([]);
  const [paxtonResources, setPaxtonResources] = useState({
    doors: [],
    access_levels: [],
  });
  const [configData, setConfigData] = useState({
    id: null,
    active: false,
    version: "net2",
    client_id: "",
    url: "",
    username: "",
    password: "",
    timezone: null,
    sync_employees: false,
    sync_visitors: false,
    doors: [],
    visitor_types: [],
  });

  /**
   * initialize the component
   */
  useCustomEffect(() => {
    fetchPaxtonResources();
    fetchVisitorTypes();
    fetchConfigData();
  }, []);

  /**
   * This method will fetch the Salto resources from the API
   */
  async function fetchPaxtonResources() {
    await callEvaApi(
      new EvaApiCall("integrations/paxton/resources")
        .setLoadingGroup("paxton_configs_page_fetch")
        .setAlertError(false)
        .setOnSuccess((response) => {
          setPaxtonResources(response.data.data);
        }),
    );
  }

  /**
   * This method will fetch the visitor types from the API
   */
  async function fetchVisitorTypes() {
    await callEvaApi(
      new EvaApiCall("visitor_types")
        .setLoadingGroup("paxton_configs_page_fetch")
        .setAlertError(false)
        .setOnSuccess((response) => {
          setVisitorTypes(response.data.data.records);
        }),
    );
  }

  /**
   * This method will fetch the Paxton config data from the API
   */
  async function fetchConfigData() {
    await callEvaApi(
      new EvaApiCall("integrations/paxton/configs")
        .setLoadingGroup("paxton_configs_page_fetch")
        .setParams(getConfigFetchParams())
        .setAlertError(true)
        .setOnSuccess((response) => {
          const data = response.data.data;
          if (!data) return;

          setConfigData(getConfigDataFromApiResponse(data));
        }),
    );
  }

  /**
   * This method will return the fetch params for the config fetch call
   * @returns {object}
   */
  function getConfigFetchParams() {
    return {
      with_paxton_config_doors_data: true,
      with_paxton_config_visitor_types_data: true,
    };
  }

  /**
   * This method will map the API response to the config data structure
   * @param {object} data
   * @returns {object}
   */
  function getConfigDataFromApiResponse(data) {
    return {
      id: data.id,
      active: data.active,
      version: data.version,
      client_id: data.client_id,
      url: data.url,
      username: data.username,
      password: data.password,
      timezone: data.timezone,
      sync_employees: data.sync_employees,
      sync_visitors: data.sync_visitors,
      doors: data.doors.map((door) => ({
        door_id: door.door_id,
        door_name: door.door_name,
        location_id: door.location,
      })),
      visitor_types: data.visitor_types.map((visitorType) => ({
        visitor_type_id: visitorType.visitor_type,
        access_level_id: visitorType.access_level_id,
      })),
    };
  }

  /**
   * Handles changes to the configuration data by updating the corresponding key with the new value.
   */
  function onChange(key, value) {
    setConfigData((prevConfigData) => ({
      ...prevConfigData,
      [key]: value,
    }));
  }

  /**
   * This method will submit the form
   */
  async function onSubmit() {
    await callEvaApi(
      new EvaApiCall("integrations/paxton/configs")
        .setLoadingGroup("paxton_configs_page_save")
        .setParams(getConfigFetchParams())
        .setMethod("PUT")
        .setData(configData)
        .setAlertError(true)
        .setErrorState(setSubmitError)
        .setAlertSuccess(true)
        .setOnSuccess((response) => {
          const data = response.data.data;
          if (!data) return;

          setConfigData(getConfigDataFromApiResponse(data));
          fetchPaxtonResources();
        }),
    );
  }

  return (
    <PaxtonConfigsPageView
      configData={configData}
      setConfigData={setConfigData}
      visitorTypes={visitorTypes}
      paxtonResources={paxtonResources}
      onChange={onChange}
      submitError={submitError}
      onSubmit={onSubmit}
    />
  );
}
